import { CircularProgress, Grid, Typography } from '@mui/material';
import ConfirmModal from 'components/confirm-modal';
import useToast from 'hooks/useToast';
import { FC, useState } from 'react';
import { useGetSeatChangeTermsQuery } from 'redux-store/api/billing';

const FALLBACK_WORDING =
  'A prorated charge or credit may apply immediately for the remainder of your current billing period.';

const formatCents = (cents?: number): string =>
  typeof cents === 'number' ? `$${(cents / 100).toFixed(2)}` : '—';

interface IProps {
  targetSeatCount: number;
  onConfirmed: () => unknown | Promise<unknown>;
  onClose: () => void;
}

const SummaryRow: FC<{ label: string; value: string }> = ({ label, value }) => (
  <Grid container justifyContent="space-between">
    <Typography sx={{ fontSize: '14px', color: '#6E707A' }}>{label}</Typography>
    <Typography sx={{ fontSize: '14px', color: '#23252F', fontWeight: 500 }}>{value}</Typography>
  </Grid>
);

const SeatChangeConfirmModal: FC<IProps> = ({ targetSeatCount, onConfirmed, onClose }) => {
  const showToast = useToast();
  const { data: terms, isFetching } = useGetSeatChangeTermsQuery({ targetSeatCount });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirmed();
      onClose();
    } catch (err: any) {
      showToast({
        type: 'error',
        message:
          typeof err?.data?.message === 'string'
            ? err.data.message
            : 'Unable to complete this action. Please try again or contact support for help.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const textNode = isFetching ? (
    <Grid container justifyContent="center" sx={{ py: 3 }}>
      <CircularProgress size={24} />
    </Grid>
  ) : (
    <Grid container direction="column" gap={1.25}>
      <SummaryRow
        label="Users"
        value={`${terms?.currentSeatCount ?? ''} → ${terms?.newSeatCount ?? targetSeatCount}`}
      />
      <SummaryRow
        label="Monthly amount"
        value={`${formatCents(terms?.currentAmount)} → ${formatCents(terms?.newAmount)}`}
      />
      <Typography sx={{ fontSize: '14px', color: '#6E707A', mt: '8px' }}>
        {terms?.wording || FALLBACK_WORDING}
      </Typography>
    </Grid>
  );

  return (
    <ConfirmModal
      open
      title="Confirm subscription change"
      text=""
      textNode={textNode}
      confirmText="Confirm"
      confirmBtnColor="primary"
      isLoading={isSubmitting || isFetching}
      onConfirm={handleConfirm}
      onClose={onClose}
    />
  );
};

export default SeatChangeConfirmModal;
